import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { PageHead, Badge } from "../../components/ui-bits";
import { toast } from "sonner";
import { MagnifyingGlass, Ticket } from "@phosphor-icons/react";

const STATUSES = ["Open", "In Progress", "Resolved"];
const STATUS_KEY = { Open: "overdue", "In Progress": "upcoming", Resolved: "completed" };

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [agent, setAgent] = useState("all");

  const load = () => {
    api.get("/admin/tickets").then((r) => setTickets(r.data));
    api.get("/admin/agents").then((r) => setAgents(r.data));
  };
  useEffect(() => { load(); }, []);

  const setStatusFor = async (t, s) => {
    await api.patch(`/tickets/${t.id}/status`, { status: s });
    toast.success(`Ticket marked ${s}`);
    setTickets((prev) => prev.map((x) => (x.id === t.id ? { ...x, status: s } : x)));
  };

  const filtered = useMemo(() => tickets.filter((t) => {
    const ms = status === "all" || t.status === status;
    const ma = agent === "all" || t.assignee_email === agent;
    const s = q.toLowerCase();
    const mq = !s || (t.subject || "").toLowerCase().includes(s) || (t.agent_name || "").toLowerCase().includes(s) || (t.category || "").toLowerCase().includes(s);
    return ms && ma && mq;
  }), [tickets, status, agent, q]);

  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: tickets.filter((t) => t.status === s).length }), {});

  return (
    <div>
      <PageHead eyebrow="Every Agent Task in One Board" title="Ticket Center" />

      <div className="grid grid-cols-3 gap-4 mb-6">
        {STATUSES.map((s) => (
          <button key={s} data-testid={`tc-count-${s.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setStatus(status === s ? "all" : s)}
            className={`card p-5 text-left transition-all hover:-translate-y-0.5 ${status === s ? "ring-2 ring-[#002FA7]" : ""}`}>
            <p className="label mb-1">{s}</p>
            <p className="font-head font-black text-3xl tnum">{counts[s]}</p>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888]" />
          <input data-testid="tc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subject, agent, category…"
            className="w-full border border-black/20 rounded-sm pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        </div>
        <select data-testid="tc-filter-agent" value={agent} onChange={(e) => setAgent(e.target.value)}
          className="border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
          <option value="all">All agents</option>
          {agents.map((a) => <option key={a.email} value={a.email}>{a.name}</option>)}
        </select>
        <select data-testid="tc-filter-status" value={status} onChange={(e) => setStatus(e.target.value)}
          className="border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
          <option value="all">All statuses</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead><tr className="label text-left border-b border-black/10">
            <th className="px-5 py-3">Subject</th><th className="py-3">Category</th><th className="py-3">Agent</th>
            <th className="py-3">Raised by</th><th className="py-3">Status</th><th className="py-3 text-right pr-5">Set status</th></tr></thead>
          <tbody>
            {filtered.map((t, i) => (
              <tr key={t.id} data-testid={`tc-row-${i}`} className="border-b border-black/5">
                <td className="px-5 py-3 font-medium flex items-center gap-2"><Ticket size={15} className="text-[#002FA7]" /> {t.subject}</td>
                <td className="py-3 text-[#555]">{t.category}</td>
                <td className="py-3">{t.agent_name}</td>
                <td className="py-3 text-[#888]">{t.raised_by}</td>
                <td className="py-3"><Badge status={STATUS_KEY[t.status] || "upcoming"}>{t.status}</Badge></td>
                <td className="py-3 text-right pr-5">
                  <select data-testid={`tc-status-${i}`} value={t.status} onChange={(e) => setStatusFor(t, e.target.value)}
                    className="border border-black/20 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-[#888]">No tickets match your filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
